import {
  IconAddressBook,
  IconAffiliate,
  IconArrowBackUp,
  IconCalendarEvent,
  IconCode,
  IconCreditCard,
  IconFilePencil,
  IconFileText,
  IconHeadset,
  IconLayoutDashboard,
  IconLayoutGridAdd,
  IconListCheck,
  IconLock,
  IconMail,
  IconMenu2,
  IconMessageChatbot,
  IconMessageCircle,
  IconMessageQuestion,
  IconPackages,
  IconPencilPlus,
  IconPlugConnected,
  IconRocket,
  IconSettings,
  IconUserCog,
  IconUsersGroup,
  TablerIcon,
} from "@tabler/icons-react";
import { lazy } from "react";
import { DataResponse } from "@common/features/User";
import { __ } from "@common/helpers/i18n";
import type { MenuItemsType } from "./layout/Menu";

const AutomationSidebar = lazy(
  () => import("./pages/automation/AutomationSidebar"),
);

function unreadCount(value: unknown) {
  const count = Number(value || 0);
  return Number.isFinite(count) && count > 0 ? count : false;
}

function menuIcon(Icon: TablerIcon) {
  return <Icon size={18} stroke={2} />;
}

export const generateMenu = (data: DataResponse) => {
  const menu: MenuItemsType[] = [
    {
      url: "/dashboard",
      label: __("Dashboard"),
      icon: menuIcon(IconLayoutDashboard),
    },
    {
      url: "/post",
      label: __("Publish"),
      icon: menuIcon(IconPencilPlus),
      teamPermission: "post_schedule",
    },
    {
      icon: menuIcon(IconFileText),
      teamPermission: "post_schedule",
      label: __("Contents"),
      childs: [
        {
          label: __("Contents"),
          url: "/posts",
          icon: menuIcon(IconFileText),
        },
        {
          label: __("Calendar"),
          url: "/schedule-calendar",
          icon: menuIcon(IconCalendarEvent),
        },
        {
          label: __("Drafts"),
          url: "/draft",
          icon: menuIcon(IconFilePencil),
        },
      ],
    },
    {
      url: "/comment",
      label: __("Comments"),
      icon: menuIcon(IconMessageCircle),
      permission: "comment",
      teamPermission: "comment_view",
      unread: () => unreadCount(data.masterUser!.comment_unread),
    },
    {
      url: "/inbox",
      label: __("Messages"),
      icon: menuIcon(IconMail),
      teamPermission: "inbox_view",
      unread: () => unreadCount(data.masterUser!.inbox_unread),
    },
    {
      label: __("Automation"),
      icon: menuIcon(IconMessageChatbot),
      url: "/automation",
      showSelectOnSmall: true,
      Component: AutomationSidebar,
      childs: [
        {
          label: __("Auto response"),
          url: "/response",
          icon: menuIcon(IconArrowBackUp),
        },
        {
          label: __("Form builder"),
          url: "/form",
          icon: menuIcon(IconListCheck),
        },
        {
          label: __("Persistent menu"),
          url: "/menu",
          icon: menuIcon(IconMenu2),
        },
        {
          label: __("Frequently asked questions"),
          url: "/question",
          icon: menuIcon(IconMessageQuestion),
        },
        {
          label: __("Products & services"),
          url: "/product",
          icon: menuIcon(IconPackages),
        },
        {
          label: __("Leeds"),
          url: "/leed",
          icon: menuIcon(IconAddressBook),
        },
      ],
    },
    {
      icon: menuIcon(IconSettings),
      label: __("Settings"),
      teamPermission: false,
      showSelectOnSmall: true,
      childs: [
        {
          url: "/accounts",
          label: __("Connect accounts"),
          icon: menuIcon(IconPlugConnected),
          teamPermission: false,
        },
        {
          url: "/team",
          label: __("Team Members"),
          icon: menuIcon(IconUsersGroup),
          teamPermission: false,
        },
        {
          url: "/user/profile",
          label: __("Profile settings"),
          icon: menuIcon(IconUserCog),
        },
        {
          url: "/user/security",
          label: __("Security settings"),
          icon: menuIcon(IconLock),
        },
        {
          url: "/integrations",
          label: __("title.integrations"),
          icon: menuIcon(IconLayoutGridAdd),
          teamPermission: false,
        },
        {
          url: "/order",
          label: __("title.order"),
          icon: menuIcon(IconCreditCard),
        },
        {
          url: "/settings/affiliate",
          label: __("title.affiliate"),
          icon: menuIcon(IconAffiliate),
        },
        {
          url: "/renew",
          label: __("Subscription"),
          icon: menuIcon(IconRocket),
        },
        {
          url: "/clients",
          label: __("title.developers"),
          icon: menuIcon(IconCode),
        },
        {
          url: "/ticket",
          label: __("Support ticket"),
          icon: menuIcon(IconHeadset),
          teamPermission: false,
        },
      ].filter(Boolean) as MenuItemsType["childs"],
    },
  ];

  return menu;
};
